// MFA TOTP (VLT-307 · auth endurecida). Segundo factor con app autenticadora
// (Google Authenticator, 1Password, Authy…): inscripción con QR + código de
// confirmación, y desafío al iniciar sesión cuando la cuenta lo tiene activo.
// Passkeys (WebAuthn) llegan en la siguiente fase del endurecimiento; la
// OBLIGATORIEDAD para roles staff (aal2 exigido por RLS) llega con el
// endurecimiento final — hoy es activable y se verifica en el login.
import { supabase, hasBackend } from '@/lib/supabase';
import { loadFailed } from './loadError';

export interface MfaStatus {
  enrolled: boolean;
  factorId?: string;
}

export interface EnrollStart {
  ok: boolean;
  factorId?: string;
  /** QR listo para <img src> (SVG data URI) y URI otpauth:// para manual. */
  qrCode?: string;
  secret?: string;
  uri?: string;
  /** Mensaje literal del servidor cuando falla — el propietario necesita saber
   *  QUÉ rechazó Supabase (ej. "MFA enroll is disabled"), no un genérico. */
  errorMessage?: string;
}

export interface MfaService {
  status(): Promise<MfaStatus>;
  /** Paso 1: crea el factor TOTP y devuelve QR + secreto. */
  enrollStart(): Promise<EnrollStart>;
  /** Paso 2: confirma con el código de la app — activa el factor. */
  enrollVerify(factorId: string, code: string): Promise<boolean>;
  /** Desactiva el factor (pide sesión válida; queda auditado por Supabase). */
  disable(factorId: string): Promise<boolean>;
  /** ¿La sesión actual necesita superar un desafío TOTP? (login) */
  needsChallenge(): Promise<boolean>;
  /** Resuelve el desafío del login con el código de la app. */
  verifyChallenge(code: string): Promise<boolean>;
}

// ─── Implementación real (Supabase Auth MFA) ────────────────────────────────

const realMfa: MfaService = {
  async status() {
    const { data, error } = await supabase.auth.mfa.listFactors();
    // Un fallo NO es «MFA no activado» (afirmaría algo falso de seguridad).
    if (error) loadFailed('mfa.status', error);
    const totp = data.totp.find((f) => f.status === 'verified');
    return { enrolled: Boolean(totp), factorId: totp?.id };
  },

  async enrollStart() {
    // Limpia factores a medio inscribir para no acumular basura.
    const { data: existing, error: listErr } = await supabase.auth.mfa.listFactors();
    if (listErr) console.warn('[VaulTex] mfa.listFactors:', listErr.message);
    for (const f of existing?.totp ?? []) {
      if (f.status !== 'verified') {
        const { error: unErr } = await supabase.auth.mfa.unenroll({ factorId: f.id });
        if (unErr) console.warn('[VaulTex] mfa.unenroll(pendiente):', unErr.message);
      }
    }
    // friendlyName único: GoTrue rechaza dos factores con el mismo nombre, y un
    // factor pendiente que no se pudo limpiar dejaría al usuario atascado.
    const { data, error } = await supabase.auth.mfa.enroll({
      factorType: 'totp',
      issuer: 'VaulTex',
      friendlyName: `VaulTex TOTP ${new Date().toISOString()}`,
    });
    if (error || !data) {
      if (error) console.warn('[VaulTex] mfa.enroll:', error.message);
      return { ok: false, errorMessage: error?.message ?? listErr?.message };
    }
    return { ok: true, factorId: data.id, qrCode: data.totp.qr_code, secret: data.totp.secret, uri: data.totp.uri };
  },

  async enrollVerify(factorId, code) {
    const { data: ch, error: e1 } = await supabase.auth.mfa.challenge({ factorId });
    if (e1 || !ch) {
      if (e1) console.warn('[VaulTex] mfa.challenge:', e1.message);
      return false;
    }
    const { error } = await supabase.auth.mfa.verify({ factorId, challengeId: ch.id, code });
    if (error) console.warn('[VaulTex] mfa.verify:', error.message);
    return !error;
  },

  async disable(factorId) {
    const { error } = await supabase.auth.mfa.unenroll({ factorId });
    if (error) console.warn('[VaulTex] mfa.disable:', error.message);
    return !error;
  },

  async needsChallenge() {
    const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    if (error || !data) return false;
    return data.nextLevel === 'aal2' && data.nextLevel !== data.currentLevel;
  },

  async verifyChallenge(code) {
    const { data: factors } = await supabase.auth.mfa.listFactors();
    const totp = factors?.totp.find((f) => f.status === 'verified');
    if (!totp) return false;
    return realMfa.enrollVerify(totp.id, code);
  },
};

// ─── Implementación demo (fixtures) ─────────────────────────────────────────

let demoEnrolled = false;

const DEMO_SECRET = 'VLTX DEMO SECR ET26';

const fixtureMfa: MfaService = {
  status: () => Promise.resolve({ enrolled: demoEnrolled, factorId: demoEnrolled ? 'demo-factor' : undefined }),
  enrollStart: () =>
    Promise.resolve({
      ok: true,
      factorId: 'demo-factor',
      // QR de demostración (SVG inline): en real llega el de Supabase.
      qrCode:
        'data:image/svg+xml;utf8,' +
        encodeURIComponent(
          `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 29 29" width="160" height="160"><rect width="29" height="29" fill="#fff"/><path fill="#000" d="M0 0h7v7H0zM22 0h7v7h-7zM0 22h7v7H0zM2 2h3v3H2zM24 2h3v3h-3zM2 24h3v3H2zM10 2h2v2h-2zM14 1h1v3h-1zM17 3h2v1h-2zM10 6h1v4h-1zM13 5h3v1h-3zM18 6h1v2h-1zM9 10h2v1H9zM13 9h1v3h-1zM16 10h4v1h-4zM22 9h1v2h-1zM25 10h2v1h-2zM1 13h3v1H1zM6 12h2v2H6zM10 13h2v2h-2zM15 13h1v1h-1zM18 12h1v3h-1zM21 13h3v1h-3zM26 12h2v1h-2zM3 16h1v2H3zM6 17h3v1H6zM12 16h2v1h-2zM16 16h1v3h-1zM20 17h2v2h-2zM24 16h1v2h-1zM27 17h1v3h-1zM10 20h2v2h-2zM13 21h2v1h-2zM18 20h1v2h-1zM22 21h2v1h-2zM9 24h2v1H9zM13 24h1v3h-1zM16 25h2v1h-2zM20 24h3v2h-3zM25 24h2v3h-2z"/></svg>`,
        ),
      secret: DEMO_SECRET,
      uri: 'otpauth://totp/VaulTex:demo?secret=VLTXDEMOSECRET26&issuer=VaulTex',
    }),
  enrollVerify: (_f, code) => {
    const ok = /^\d{6}$/.test(code);
    if (ok) demoEnrolled = true;
    return Promise.resolve(ok);
  },
  disable: () => {
    demoEnrolled = false;
    return Promise.resolve(true);
  },
  needsChallenge: () => Promise.resolve(false),
  verifyChallenge: (code) => Promise.resolve(/^\d{6}$/.test(code)),
};

export const mfaService: MfaService = hasBackend ? realMfa : fixtureMfa;
